// ── Auto-edit queue ───────────────────────────────────────────────────────
// Collects deferred rewrites of the editor content and drains them only on
// three triggers: cursor line change, view-mode toggle, note switch.
//
// Transforms are pure, idempotent `(text, ctx) -> text` rewrites; actions are
// async side-effectful callbacks that may mutate storage.  Transforms run
// first (in registration order), then actions.  The table-cleanup transform
// imports _cleanupMarkdownTables statically from the note-editor-free leaf
// table-helpers.js (no cycle); applyDailyNoteDateCodes / checkAttachmentRenames
// stay closure-resolved (note-manager, which imports back into this stack).

import {
  getCurrentFileName,
  getLastSavedContent,
  getLoadNoteGeneration,
} from '../core/app-state.js';
import { applyDailyNoteDateCodes, checkAttachmentRenames } from '../notes/note-manager.js';
import { autoScheduleTasksInText, resolveNaturalDatesInText } from '../utils/date-nl.js';
import { diffTransforms } from './auto-edit-diff.js';
import { NoteEditor } from './note-editor.js';
import { _cleanupMarkdownTables } from './table-helpers.js';

(() => {
  const transforms: any = [];
  const actions: any = [];

  let isDirty = false;
  let _flushing = false;

  function registerTransform(name: string, fn: (...args: any[]) => any): void {
    transforms.push({ name, fn });
  }
  function registerAction(name: string, fn: (...args: any[]) => any): void {
    actions.push({ name, fn });
  }

  function markDirty(): void {
    isDirty = true;
  }

  async function flush({ reason, generation, touchedLine }: any = {}): Promise<void> {
    if (_flushing || !isDirty) return;
    if (NoteEditor.isReadOnly()) {
      isDirty = false;
      return;
    }
    if (!getCurrentFileName()) {
      isDirty = false;
      return;
    }

    _flushing = true;
    try {
      const before = NoteEditor.getContent();
      // #21 region-scoping: the table-cleanup transform normalizes only the
      // table block containing `scopeLine`. A line-change flush passes the line
      // the cursor LEFT; other flushes (save / note-switch / attach) omit it, so
      // we default to the CURRENT cursor line — clean the table the cursor sits
      // in on save, but never reflow a hand-aligned table the cursor never
      // entered. A negative/omitted line makes _cleanupMarkdownTables a no-op.
      const scopeLine =
        typeof touchedLine === 'number' && touchedLine >= 0 ? touchedLine : currentLineIndex();
      const ctx = {
        fileName: getCurrentFileName(),
        prevSavedContent: getLastSavedContent(),
        reason,
        touchedLine: scopeLine,
      };

      // Run the transform chain (diffTransforms keeps its per-transform catch so
      // a throwing transform is skipped, not fatal). The change is applied
      // silently — no flash / micro-toast (#12); attribution is unused now.
      const { text } = diffTransforms(before, transforms, ctx);
      if (text !== before) {
        // No explicit selection — replaceAll's prefix/suffix-trim lets CM6
        // map the existing selection through the change automatically, so
        // a cursor in the middle of the doc stays anchored to the same
        // logical character even when transforms shorten the doc.
        NoteEditor.replaceAll(text);
      }

      for (const { fn } of actions) {
        if (
          generation !== undefined &&
          typeof getLoadNoteGeneration() !== 'undefined' &&
          generation !== getLoadNoteGeneration()
        )
          return;
        try {
          await fn(ctx);
        } catch (e: any) {
          console.error('[auto-edit-queue] action failed:', e);
        }
      }
    } finally {
      isDirty = false;
      _flushing = false;
    }
  }

  // Line-change tracker state (mutated by app-init listeners via resetLineTracker).
  let _lastLineIndex = -1;
  function resetLineTracker(idx = -1): void {
    _lastLineIndex = idx;
  }
  function currentLineIndex(): any {
    return NoteEditor.getCursor().line;
  }
  function onSelectionChange(): void {
    const li = currentLineIndex();
    if (li === _lastLineIndex) return;
    // Capture the line the cursor LEFT before reassigning — that's the region
    // the table-cleanup transform (#21) should normalize, so a table the cursor
    // just exited is tidied while tables elsewhere in the doc stay untouched.
    const leftLine = _lastLineIndex;
    _lastLineIndex = li;
    flush({ reason: 'line-change', touchedLine: leftLine });
  }

  // ── Built-in registrations ──────────────────────────────────────────────
  // daily-date-code + attachment-rename use note-manager's applyDailyNoteDateCodes
  // / checkAttachmentRenames via the static import above. note-manager reaches
  // THIS stack only through window.AutoEditQueue (a runtime read, not an ESM
  // import), so the auto-edit-queue → note-manager edge stays one-way (no cycle).
  // table-cleanup uses the static _cleanupMarkdownTables import (the
  // note-editor-free table-helpers leaf — no cycle).

  registerTransform('daily-date-code', (text: any, ctx: any) =>
    applyDailyNoteDateCodes(text, ctx.fileName)
  );

  // nl-date: rewrite trailing natural-language date/time phrases after a `>`
  // marker into the canonical `> YYMMDD [HHMM HHMM]` (#36). task-auto: silently
  // convert a task line ending in a time/date phrase (no `>`) into a canonical
  // schedule line, inheriting the date from the nearest scheduled line above
  // (canonicalized by nl-date first) or the note title (#10). Both run after
  // daily-date-code (canonical output → the isScheduledLine short-circuit makes
  // them fixpoints) and before table-cleanup.
  registerTransform('nl-date', (text: any) => resolveNaturalDatesInText(text, new Date()));
  registerTransform('task-auto', (text: any, ctx: any) =>
    autoScheduleTasksInText(text, ctx.fileName, new Date())
  );

  registerTransform('table-cleanup', (text: any, ctx: any) =>
    _cleanupMarkdownTables(text, ctx.touchedLine)
  );

  registerAction('attachment-rename', async (ctx: any) => {
    await checkAttachmentRenames(ctx.prevSavedContent, NoteEditor.getContent(), ctx.fileName);
  });

  window.AutoEditQueue = {
    markDirty,
    flush,
    resetLineTracker,
    currentLineIndex,
    onSelectionChange,
    registerTransform,
    registerAction,
  };
})();

// Phase 4.5: ES-module export. The IIFE above keeps the
// window.AutoEditQueue back-compat shim until 4.7.
export const AutoEditQueue = window.AutoEditQueue;
